import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button } from 'antd';
import type { ButtonProps } from 'antd';
import { TbBolt, TbExternalLink } from 'react-icons/tb';

import { toast } from '@/shared/components/toasts/PlatformToast';
import { WorkspaceDomainConnectSetupIntent } from '@/types';

export interface DomainConnectSetupButtonProps extends Pick<ButtonProps, 'disabled' | 'size' | 'style' | 'type'> {
    children?: ReactNode;
    onReturn?: () => Promise<void> | void;
    prepareIntent: () => Promise<WorkspaceDomainConnectSetupIntent>;
}

const getManualDescription = (intent: WorkspaceDomainConnectSetupIntent) => {
    if (intent.reason === 'integration_not_configured') {
        return 'Automatic DNS setup is not configured yet. Use the DNS records shown below.';
    }

    if (intent.reason === 'template_not_supported' && intent.provider) {
        return `${intent.provider.displayName} does not support this Countercyclical template yet. Open its DNS settings and use the records shown below.`;
    }

    return 'This DNS provider does not currently support automatic setup. Use the DNS records shown below.';
};

const safelyCloseWindow = (popup: Window | null) => {
    try {
        popup?.close();
    } catch (error) {
        captureException(error);
    }
};

const safelyResizeWindow = (popup: Window, width: number, height: number) => {
    try {
        popup.resizeTo?.(width, height);
    } catch (error) {
        captureException(error);
    }
};

const requireSecureProviderUrl = (value: string) => {
    const url = new URL(value);

    if (url.protocol !== 'https:' || url.username || url.password || (url.port && url.port !== '443')) {
        throw new Error('Domain Connect returned an unsafe provider URL.');
    }

    return value;
};

const DomainConnectSetupButton: FC<DomainConnectSetupButtonProps> = ({
    children = 'Set Up Automatically',
    disabled,
    onReturn,
    prepareIntent,
    size = 'small',
    style,
    type = 'default',
}) => {
    const [loading, setLoading] = useState(false);
    const focusHandlerRef = useRef<(() => void) | undefined>(undefined);
    const focusTimerRef = useRef<number | undefined>(undefined);
    const requestInFlightRef = useRef(false);

    const clearReturnWatcher = () => {
        if (focusTimerRef.current !== undefined) {
            window.clearTimeout(focusTimerRef.current);
            focusTimerRef.current = undefined;
        }
        if (focusHandlerRef.current) {
            window.removeEventListener('focus', focusHandlerRef.current);
            focusHandlerRef.current = undefined;
        }
    };

    useEffect(() => clearReturnWatcher, []);

    const watchForReturn = () => {
        clearReturnWatcher();
        focusTimerRef.current = window.setTimeout(() => {
            const handleFocus = () => {
                clearReturnWatcher();
                void Promise.resolve(onReturn?.()).catch((error) => {
                    captureException(error);
                    toast.error('DNS setup returned, but verification could not be refreshed.');
                });
            };

            focusHandlerRef.current = handleFocus;
            window.addEventListener('focus', handleFocus, { once: true });
        }, 750);
    };

    const handleSetup = async () => {
        if (requestInFlightRef.current) {
            return;
        }

        requestInFlightRef.current = true;
        clearReturnWatcher();
        let popup: Window | null = null;

        try {
            popup = window.open('about:blank', 'countercyclical-domain-connect', 'popup=yes,width=750,height=750');

            if (popup) {
                popup.opener = null;
            }

            setLoading(true);
            const intent = await prepareIntent();

            if (intent.status === 'available' && intent.setupUrl) {
                if (!popup) {
                    toast.error('Automatic DNS setup was blocked', {
                        description: 'Allow pop-ups for Countercyclical, then try again.',
                    });
                    return;
                }

                if (intent.window) {
                    safelyResizeWindow(popup, intent.window.width, intent.window.height);
                }
                popup.location.replace(requireSecureProviderUrl(intent.setupUrl));
                watchForReturn();
                toast.info(`Continue securely with ${intent.provider?.displayName ?? 'your DNS provider'}`, {
                    description: 'Review and approve the DNS records there. Verification refreshes when you return.',
                });
                return;
            }

            if (intent.controlPanelUrl && popup) {
                if (intent.window) {
                    safelyResizeWindow(popup, intent.window.width, intent.window.height);
                }
                popup.location.replace(requireSecureProviderUrl(intent.controlPanelUrl));
                watchForReturn();
            } else {
                safelyCloseWindow(popup);
            }

            toast.info('Manual DNS setup required', {
                description: getManualDescription(intent),
                icon: <TbExternalLink />,
            });
        } catch (error) {
            safelyCloseWindow(popup);
            captureException(error);
            toast.error('Automatic DNS setup is temporarily unavailable', {
                description: 'Use the DNS records shown below or try again in a moment.',
            });
        } finally {
            requestInFlightRef.current = false;
            setLoading(false);
        }
    };

    return (
        <Button
            disabled={disabled}
            icon={<TbBolt />}
            loading={loading}
            size={size}
            style={style}
            type={type}
            onClick={() => {
                void handleSetup();
            }}
        >
            {children}
        </Button>
    );
};

export default DomainConnectSetupButton;
